import { notifyUnauthorized } from "../lib/queryClient";

type TokenGetter = () => string | undefined | null;

// Registered by authStore so this module stays decoupled from the store (avoids
// a circular import). Used to attach the Bearer token to authenticated requests.
let tokenGetter: TokenGetter | null = null;
export const setTokenGetter = (fn: TokenGetter): void => {
  tokenGetter = fn;
};

export interface ApiErrorData {
  error?: string;
  message?: string;
  errors?: string[] | Record<string, string[]>;
  [key: string]: unknown;
}

export class ApiError extends Error {
  status: number;
  data: ApiErrorData;
  constructor(message: string, status: number, data: ApiErrorData = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

const extractMessage = (data: ApiErrorData | string | null): string => {
  if (!data) return "";
  if (typeof data === "string") return data;
  const value = data.error ?? data.message ?? data.errors;
  return Array.isArray(value) ? value.join(", ") : value ? String(value) : "";
};

export interface RequestOptions {
  method?: string;
  body?: unknown;
  auth?: boolean;
  token?: string;
  // Skip the global 401 → logout-and-redirect handler. Used for background
  // checks (e.g. boot session validation) that must fail silently.
  silent?: boolean;
}

export interface RequestResult<T> {
  data: T;
  headers: Headers;
  status: number;
}

// Shared fetch core: auth header, 401 handling, uniform ApiError.
// Returns the raw Response so JSON and blob consumers share one path.
async function fetchChecked(
  path: string,
  { method = "GET", body = undefined, auth = false, token = undefined, silent = false }: RequestOptions = {}
): Promise<Response> {
  const base = process.env.REACT_APP_LOOPHOLE_API;
  const headers: Record<string, string> = {};
  const jwt = token ?? (auth && tokenGetter ? tokenGetter() : undefined);
  if (jwt) headers["Authorization"] = `Bearer ${jwt}`;

  let payload: BodyInit | undefined;
  if (body !== undefined) {
    if (body instanceof FormData) {
      payload = body;
    } else {
      headers["Content-Type"] = "application/json";
      payload = JSON.stringify(body);
    }
  }

  const response = await fetch(`${base}${path}`, {
    method,
    headers,
    body: payload,
  });

  // Invalid credentials (401 on a request with no token, e.g. login) must NOT
  // be treated as an expired session. Silent requests never notify.
  if (response.status === 401 && jwt && !silent) {
    notifyUnauthorized();
  }

  if (!response.ok) {
    const errorData = (await response.json().catch(() => ({}))) as ApiErrorData;
    throw new ApiError(
      extractMessage(errorData) || `Request failed (${response.status})`,
      response.status,
      errorData
    );
  }

  return response;
}

// Core request: performs the fetch, uniform error handling, and returns the
// parsed body + response headers. Both `request` and `requestWithMeta` build on
// this so the behaviour (auth, 401 handling, errors) lives in one place.
async function requestRaw<T>(
  path: string,
  options: RequestOptions = {}
): Promise<RequestResult<T>> {
  const response = await fetchChecked(path, options);

  if (response.status === 204) {
    return { data: null as T, headers: response.headers, status: response.status };
  }
  const text = await response.text();
  const data = text ? (JSON.parse(text) as T) : (null as T);
  return { data, headers: response.headers, status: response.status };
}

// Central request helper. `auth` true attaches the Bearer token from the store
// (or an explicit `token` override). Throws a uniform ApiError on non-2xx and
// triggers the 401 handler when an *authenticated* request is rejected.
export async function request<T = unknown>(
  path: string,
  options: RequestOptions = {}
): Promise<T> {
  const { data } = await requestRaw<T>(path, options);
  return data;
}

// Like `request`, but also returns response headers — used for paginated
// endpoints that expose Total-Count / Per-Page / Page / Link.
export async function requestWithMeta<T = unknown>(
  path: string,
  options: RequestOptions = {}
): Promise<RequestResult<T>> {
  return requestRaw<T>(path, options);
}

// Like `request`, but resolves the raw Blob — used for authenticated file
// downloads and previews (iframes and anchors can't send the Bearer token).
export async function requestBlob(
  path: string,
  options: RequestOptions = {}
): Promise<Blob> {
  const response = await fetchChecked(path, options);
  return response.blob();
}
